import "../../../styles/mapStyles/Popup.css";
import { CROP_OPTIONS, FERTILIZER_BLEND_OPTIONS } from "../../../constants/cropAndBlendOptions";
import { useState } from "react";
import calculateCropFertilizerApplication from "../../../utils/fertilizerRecommendationCalculation";
import OfraFertilizerRateCalculator from "./OfraFertilizerRateCalculator";
import { useTranslation } from "react-i18next";

function CropBasedFertilizerRecommendation({
  recommendation,
  selectedCrop,
  setSelectedCrop,
  setCropRecommendation,
  cropRecommendation,
  selectedBlend,
  setSelectedBlend,
  // OFRA calculator props — passed from SoilPopupContent
  ofraContainerValue,
  setOfraContainerValue,
  ofraFertilizerTypeValue,
  setOfraFertilizerTypeValue,
  ofraFertilizerRateValue,
  setOfraFertilizerRateValue,
  ofraApplicationMethodValue,
  setOfraApplicationMethodValue,
}) {
  // Tracks which recommendation method the user picked "automatic" | "manual"
  const [recommendationMode, setRecommendationMode] = useState("automatic");
  // Both dropdowns must have a value before the button appears
  const bothSelected = selectedCrop !== "" && selectedBlend !== "";

  // Switches between automatic and manual mode.
  const handleModeSwitch = (mode) => setRecommendationMode(mode);

  // Gives the crop-based fertilizer recommendation based on the selected crop, blend, and soil data.
  const handleGetCropFertilizerRecommendation = () => {
    calculateCropFertilizerApplication(
      recommendation,
      selectedCrop,
      selectedBlend,
      setCropRecommendation,
    );
  };

  const { t } = useTranslation();

  let cropBasedRecommendation;

  switch (cropRecommendation) {
    // If the calculation could not be completed, show an error message.
    case "Unable to calculate recommendation for the selected crop and blend.":
      cropBasedRecommendation = t("cropFertilizerRecommendation.unableToCalculate");
      break; 

    // If the calculation determines that no fertilizer is needed, show a different message.
    case "No fertilizer application is required.":
      cropBasedRecommendation = t("cropFertilizerRecommendation.noApplicationRequired");
      break;

    default:
      cropBasedRecommendation = `${t("common.apply")} ${cropRecommendation} ${t("common.perHectare")}`;
      break;
  }

  return (
    <>
      <strong className="results-title">{t("soil.cropBasedFertilizerRecommendation")}</strong>

      {/* Toggle row — Automatic vs Manual */}
      <div className="recommendation-mode-toggle">
        <button
          className={`mode-btn ${recommendationMode === "automatic" ? "mode-btn--active" : ""}`}
          onClick={() => handleModeSwitch("automatic")}
        >
          {t("common.automatic")}
        </button>
        <button
          className={`mode-btn ${recommendationMode === "manual" ? "mode-btn--active" : ""}`}
          onClick={() => handleModeSwitch("manual")}
        >
          {t("common.manual")}
        </button>
      </div>

      {/* ── Automatic mode — dropdowns and calculate button ── */}
      {recommendationMode === "automatic" && (
        <div className="auto-calculator-container">
          <p className="auto-calculator-label">
            {t("soil.selectYourCropBlend")}
          </p>

          {/* Crop dropdown */}
          <div className="calculator-field">
            <label className="calculator-field-label">{t("common.crop")}</label>
            <select
              className="calculator-select"
              value={selectedCrop}
              onChange={(e) => {
                setSelectedCrop(e.target.value);
                setCropRecommendation(null); // clear result on change
              }}
            >
              <option value="" disabled>
                {t("soil.selectACrop")}
              </option>
              {CROP_OPTIONS.map((crop) => (
                <option key={crop.value} value={crop.value}>
                  {t(crop.label)}
                </option>
              ))}
            </select>
          </div>

          {/* Fertilizer blend dropdown */}
          <div className="calculator-field">
            <label className="calculator-field-label">{t("common.fertilizerBlend")}</label>
            <select
              className="calculator-select"
              value={selectedBlend}
              onChange={(e) => {
                setSelectedBlend(e.target.value);
                setCropRecommendation(null); // clear result on change
              }}
            >
              <option value="" disabled>
                {t("soil.selectABlend")}
              </option>
              {FERTILIZER_BLEND_OPTIONS.map((blend) => (
                <option key={blend.value} value={blend.value}>
                  {blend.label}
                </option>
              ))}
            </select>
          </div>

          {/* Calculated recommendation result */}
          {cropRecommendation && (
            <div className="crop-recommendation-result">
              <strong className="results-title">{t("common.result")}</strong>
              <p className="crop-recommendation-text">{cropBasedRecommendation}</p>
            </div>
          )}

          {/* Get recommendation button — only appears when both dropdowns are filled */}
          {bothSelected && (
            <button className="get-recommendation-btn" onClick={handleGetCropFertilizerRecommendation}>
              {t("soil.getRecommendation")}
            </button>
          )}
        </div>
      )}

      {/* ── Manual mode — OFRA Excel link ── */}
      {recommendationMode === "manual" && (
        <OfraFertilizerRateCalculator
          containerValue={ofraContainerValue}
          setContainerValue={setOfraContainerValue}
          fertilizerTypeValue={ofraFertilizerTypeValue}
          setFertilizerTypeValue={setOfraFertilizerTypeValue}
          fertilizerRateValue={ofraFertilizerRateValue}
          setFertilizerRateValue={setOfraFertilizerRateValue}
          applicationMethodValue={ofraApplicationMethodValue}
          setApplicationMethodValue={setOfraApplicationMethodValue}
        />
      )}
    </>
  );
}

export default CropBasedFertilizerRecommendation;
